import { Component, Event, EventEmitter, h /*getAssetPath*/, Listen, Prop, State } from '@stencil/core';
import store from '../../../helpers/store';
import { FlowStatus } from '../../../models/FlowStatus';
import { FlowSteps } from '../../../models/FlowSteps';
import { base64Size, blobToBase64, delay, getLogMessage } from '../../../utils/utils';
import { BaseComponent } from '../../base-component';
import { CaptureSide, CaptureUploadTypes } from '../../../models/CaptureFlow';
import { ApiCall } from '../../../helpers/ApiCall';
import { Cameras } from '../../../helpers/Cameras';
import AppInsightsService from '../../../helpers/AppInsightsService';
import { LoggerService } from '../../../helpers/LoggerService';

// import { IDPose } from '../../libs/IDML5Detector/IDPose';

@Component({
  tag: 'process-id',
  styleUrl: 'process-id.css',
  shadow: false,
})
export class ProcessId {
  private captureRetryCount: number;
  private recordingResultCount: number = 0;
  private currentStep: FlowSteps;
  private errorReason: string;
  private verificationReceived: number;

  @Prop() public mode: string;
  @State() private showTimeout: boolean;
  @State() private showInvalid: boolean;
  @State() private flow: CaptureSide;
  @State() private capture: File;
  @State() private uploadingPhoto: boolean = false;
  @State() private uploadingRecording: boolean = false;

  @Event({
    eventName: 'apiError',
  })
  apiErrorEvent: EventEmitter<any>;

  private appInsights = AppInsightsService.getInstance();
  constructor() {}

  componentWillLoad() {
    this.captureRetryCount = 0;
    this.initFlow();
    this.appInsights.trackEvent('ProcessId | init', { requestId: store.requestId });
  }

  initFlow() {
    if (this.mode == 'front') {
      this.currentStep = FlowSteps.CiFront;
      this.flow = {
        fileName: 'idImage.png',
        recName: 'idVideo.',
        photoType: CaptureUploadTypes.IdFront,
        recType: CaptureUploadTypes.IdFrontVideo,
        imageSize: 0,
        videoSize: 0,
      };
    } else if (this.mode == 'back') {
      this.currentStep = FlowSteps.CiBack;
      this.flow = {
        fileName: 'idBackImage.png',
        recName: 'idBackVideo.',
        photoType: CaptureUploadTypes.IdBack,
        recType: CaptureUploadTypes.IdBackVideo,
        imageSize: 0,
        videoSize: 0,
      };
    } else if (this.mode == 'tilt') {
      this.currentStep = FlowSteps.CiTilt;
      this.flow = {
        fileName: 'idTiltImage.png',
        recName: 'idTiltVideo.',
        photoType: CaptureUploadTypes.None,
        recType: CaptureUploadTypes.TiltVideo,
        imageSize: 0,
        videoSize: 0,
      };
    }
    LoggerService.log('process-id | initFlow');
  }

  @Listen('timeElapsed')
  timeElapsed() {
    this.errorReason = 'Timeout';
    this.showTimeout = true;
  }

  @Listen('captureErrorDone')
  captureErrorDone() {
    LoggerService.log('process-id | captureErrorDone');
    if (this.showTimeout == true) {
      this.showTimeout = false;
    }
    if (this.showInvalid == true) {
      this.showInvalid = false;
    }
  }

  @Listen('photoIdCapture')
  async captureIdImage(event) {
    LoggerService.logMulti('process-id | captureIdImage', this.mode);
    if (this.mode == 'tilt') return;
    try {
      let idPhoto = event.detail;
      if (idPhoto.size == 0) {
        LoggerService.log('process-id | captureIdImage | Empty id photo');
        await ApiCall.instance.AddLog({ message: 'Empty id photo', blobData: idPhoto }, await getLogMessage());
        this.triggerErrorFlow();
        return;
      }

      this.capture = new File([idPhoto], this.flow.fileName, { type: 'image/png' });
    } catch (e) {
      LoggerService.logMulti('process-id | captureIdImage | Error processing id photo', e);
      this.triggerErrorFlow();
    }
    this.uploadCapture();
  }

  async uploadCapture() {
    try {
      this.uploadingPhoto = true;

      let b64Data: string = await blobToBase64(this.capture);
      this.flow.imageSize = base64Size(b64Data);

      LoggerService.log('process-id | captureIdImage | uploadResult: ' + uploadResult);
      var uploadResult = await ApiCall.instance.UploadFileForRequestB64(store.requestId, this.flow.photoType, b64Data);

      if (uploadResult.isValid) {
        store.flowStatus = FlowStatus[uploadResult.state];
      } else if (uploadResult.isBlurry) {
        this.uploadingPhoto = false;
        this.triggerBlurFlow();
      } else {
        this.uploadingPhoto = false;
        this.switchCamera();
        this.triggerErrorFlow();
      }

      this.capture = null;
    } catch (e) {
      this.apiErrorEvent.emit(e);
    }
  }

  @Listen('recordingIdCapture')
  async capturedIdRecording(event) {
    if (!store.videoRecording) {
      this.uploadingRecording = true;
      return;
    }
    try {
      let idRecording: Blob = event.detail;
      LoggerService.logMulti('process-id | capturedIdRecording | size: ' + idRecording.size + ' type: ' + idRecording.type);
      if (idRecording.size == 0 && this.recordingResultCount == 0) {
        this.recordingResultCount++;
        return;
      }
      if (idRecording.size == 0) {
        LoggerService.logMulti('process-id | capturedIdRecording | Empty ID recording');
        this.triggerErrorFlow();
        return;
      }
      this.uploadingRecording = true;
      let mimeType = idRecording.type == Cameras.mp4MimeType.type ? Cameras.mp4MimeType : Cameras.webmMimeType;

      let captureRec = new File([idRecording], this.flow.recName + mimeType.extension, { type: mimeType.type });
      let b64Data: string = await blobToBase64(captureRec);
      this.flow.videoSize = base64Size(b64Data);
      var uploadResult = await ApiCall.instance.UploadFileForRequestB64(store.requestId, this.flow.recType, b64Data);
      LoggerService.logMulti('process-id | capturedIdRecording | uploadResult: ' + uploadResult);
      if (uploadResult.saved) {
        store.flowStatus = FlowStatus[uploadResult.state];
      } else {
        this.uploadingRecording = false;
        this.triggerErrorFlow();
      }
      this.recordingResultCount = 0;
    } catch (e) {
      this.apiErrorEvent.emit(e);
    }
  }

  @Listen('verificationFinished')
  async verificationFinished() {
    LoggerService.log('process-id | verificationFinished');

    this.verificationReceived = Date.now();
    while (Date.now() - this.verificationReceived < 90000) {
      await delay(2000);
      if (this.showInvalid || this.showTimeout) return;
    }

    this.triggerUploadFailedFlow();

    LoggerService.log('process-id | verificationFinished | waitingFinished ');
  }

  private triggerUploadFailedFlow() {
    this.errorReason = 'Upload Failed';
    this.showInvalid = true;
  }

  async componentDidLoad() {
    LoggerService.log('process-id | componentDidLoad');
    await BaseComponent.initialize(this.currentStep);
  }

  private triggerErrorFlow() {
    this.errorReason = 'Invalid';
    this.showInvalid = true;
  }

  private triggerBlurFlow() {
    this.errorReason = 'Blurred';
    this.showInvalid = true;
  }

  async disconnectedCallback() {
    LoggerService.log('process-id | disconnectedCallback');
    await BaseComponent.finalize(this.currentStep);
  }

  switchCamera() {
    LoggerService.log('process-id | switchCamera');
    if (this.captureRetryCount == 1) {
      Cameras.switchCamera();
      this.captureRetryCount = 0;
    } else {
      this.captureRetryCount++;
    }
  }

  render() {
    let error = <capture-error type="ID" reason={this.errorReason} error={`image: ${this.flow.imageSize}mb video: ${this.flow.videoSize}mb`}></capture-error>;
    let capture = <id-auto-capture capture-mode={this.mode}></id-auto-capture>;
    let upload = <uploading-data type="id"></uploading-data>;

    if (this.uploadingPhoto && this.uploadingRecording) return upload;

    if (this.showInvalid || this.showTimeout) return error;

    return capture;
  }
}
